<template>
    <div class="benchmarking-competitor-filters">
        <div class="competitors-header display-flex align-items-center">
            <p v-if="selectedTab === 'momentum'" class="competitors-count small">
                Companies - {{ filteredCompetitorsCount }} of {{ totalCompetitorsCount }}
            </p>
            <p v-else class="competitors-count small">&nbsp;</p>
        </div>
        <div class="quick-filters display-flex mt-sm">
            <button
                v-for="filter in quickFilters.filter((f) => f.value !== 'watchlist')"
                :key="filter.value"
                class="filter-btn"
                :class="{ active: selectedFilter === filter.value }"
                @click="selectFilter(filter.value)"
            >
                {{ filter.label }}
            </button>

            <div class="preset-dropdown-btn">
                <button
                    class="filter-btn dropdown-toggle"
                    :class="{ active: selectedFilter === 'watchlist' }"
                    @click="togglePresetDropdown"
                >
                    Watchlist {{ selectedWatchlist ? '| ' + selectedWatchlist.itemText : '' }}
                    <font-awesome-icon :icon="['fas', 'caret-down']" class="ml-sm" />
                </button>
                <div v-if="showPresetDropdown" class="preset-dropdown">
                    <div v-if="loadingWatchlists" class="dropdown-loading">
                        Loading watchlists...
                    </div>
                    <div v-else-if="watchlistOptions.length === 0" class="dropdown-empty">
                        No watchlists found
                    </div>
                    <p
                        v-else
                        v-for="watchlist in watchlistOptions"
                        :key="watchlist.itemValue"
                        class="dropdown-item"
                        @click="selectWatchlist(watchlist)"
                    >
                        {{ watchlist.itemText }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useDatasetStore } from '@/stores'
import { useAuth } from '@/auth/authPlugin'
import { useFlag } from '@unleash/proxy-client-vue'

const showAcquisitionTargets = useFlag('ipAlpha.showAcquisitionTargets')
const showPotentialBuyers = useFlag('ipAlpha.showPotentialBuyers')

const props = defineProps({
    totalCompetitorsCount: {
        type: Number,
        default: 0,
    },
    filteredCompetitorsCount: {
        type: Number,
        default: 0,
    },
    selectedTab: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['filter-selected', 'watchlist-selected'])

const { user } = useAuth()
const datasetStore = useDatasetStore()

const selectedFilter = ref('all')
const filterUpdater = ref(0)
const loadingWatchlists = ref(false)
const watchlistOptions = ref([])
const selectedWatchlist = ref(null)
const showPresetDropdown = ref(false)

const quickFilters = ref([
    { label: 'All', value: 'all' },
    { label: 'Direct competitors', value: 'competitors' },
    ...(showAcquisitionTargets ? [{ label: 'Acquisition targets', value: 'acquisition' }] : []),
    ...(showPotentialBuyers ? [{ label: 'Potential buyers', value: 'potential_buyers' }] : []),
    { label: 'Public', value: 'public' },
    { label: 'Private', value: 'private' },
    { label: 'Gov/Edu', value: 'govedu' },
    { label: 'Early stage', value: 'early_stage' },
    { label: 'Watchlist', value: 'watchlist' },
])

const selectFilter = (filter) => {
    const previousFilter = selectedFilter.value
    selectedFilter.value = filter
    showPresetDropdown.value = false
    if (previousFilter === 'watchlist' && filter !== 'watchlist') {
        selectedWatchlist.value = null
        emit('watchlist-selected', null)
    }
    emit('filter-selected', filter)
}

const togglePresetDropdown = () => {
    if (showPresetDropdown.value) {
        showPresetDropdown.value = false
        if (selectedFilter.value === 'watchlist' && !selectedWatchlist.value) {
            selectedFilter.value = 'all'
            emit('filter-selected', 'all')
        }
    } else {
        showPresetDropdown.value = true
        selectedFilter.value = 'watchlist'
        emit('filter-selected', 'watchlist')
    }
}

const closeDropdownOnOutsideClick = (event) => {
    if (showPresetDropdown.value) {
        const dropdownElement = document.querySelector('.preset-dropdown-btn')
        if (dropdownElement && !dropdownElement.contains(event.target)) {
            showPresetDropdown.value = false
            if (selectedFilter.value === 'watchlist' && !selectedWatchlist.value) {
                selectedFilter.value = 'all'
                emit('filter-selected', 'all')
            }
        }
    }
}

const getUserWatchLists = async () => {
    loadingWatchlists.value = true

    try {
        let callData = await datasetStore.getDatasets([], user.value.id)
        if (callData && callData.results) {
            watchlistOptions.value = callData.results.map((item) => {
                return {
                    itemText: item.dataset_name,
                    itemValue: item.dataset_pk,
                    itemDesc: item.dataset_description,
                    type: 'watchlist',
                }
            })
        }
    } catch (err) {
        console.error('Failed to load watchlists', err)
    } finally {
        filterUpdater.value++
        loadingWatchlists.value = false
    }
}

const selectWatchlist = (watchlist) => {
    selectedWatchlist.value = watchlist
    showPresetDropdown.value = false
    emit('watchlist-selected', watchlist)
}

onMounted(() => {
    getUserWatchLists()
    document.addEventListener('click', closeDropdownOnOutsideClick)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', closeDropdownOnOutsideClick)
})
</script>

<style lang="scss" scoped>
.benchmarking-competitor-filters {
    .competitors-header {
        margin-bottom: 8px;
    }

    .quick-filters {
        gap: 10px;
        flex-wrap: wrap;
        position: relative;
    }

    .filter-btn {
        padding: 6px 16px;
        border-radius: 20px;
        background-color: #f0f2f5;
        border: none;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s ease;

        &:hover {
            background-color: #e4e6eb;
        }

        &.active {
            background-color: #1a3668;
            color: white;
        }
    }

    .preset-dropdown-btn {
        position: relative;
        display: inline-block;

        .dropdown-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 80px;
            max-width: 250px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .preset-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 8px;
        z-index: 1000;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        width: 250px;
        max-height: 300px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
    }

    .dropdown-item {
        padding: 10px 16px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: #f0f2f5;
        }
    }

    .dropdown-loading,
    .dropdown-empty {
        padding: 16px;
        text-align: center;
        color: #666;
    }
}
</style>
